import { CreateAlternative } from "@/components/pageParts/project/CreateAlternative";
import { useCreateAlternative } from "@/hooks/alternative/useCreateAlternative";
import { RootState } from "@/store/store";
import { setToast } from "@/store/systemSlice";
import { CreateAlternativeRequest } from "@/types/alternative";
import { FilesTypes, UploadFilesTableData } from "@/types/file";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import styles from "./css/CreateUpdateAlternativePage.module.css";
import { BackButton } from "@/components/Buttons/BackButton";
import { currentDate } from "@/utils/date";
import { Button } from "@/components/Buttons";
import { AlternativeData } from "@/types/alternative";
import { useUpdateAlternative } from "@/hooks/alternative/useUpdateAlternative";
import { useGetAlternative } from "@/hooks/alternative/useGetAlternative";
import { LoadingPageAnimation } from "@/components/addons/LoadingPageAnimation";
import { DeleteIcon } from "@/icons/DeleteIcon";
import { useDeleteAlternative } from "@/hooks/alternative/useDeleteAlternative";
import { getEntityIdPair } from "@/utils/getEntityPairId";
import { useExtractModelData } from "@/hooks/alternative/useExtractModelData";
import { useFetchCity } from "@/utils/fetchData";
import { useFetchAllEntities } from "@/utils/fetchData";

export const UpdateAlternativePage = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const {t} = useTranslation("UpdateAlternativePage");
    const {entityName, cityId, projectId, alternativeId} = useParams<{entityName: string, cityId: string, projectId: string, alternativeId: string}>();
    const city = useSelector((state: RootState) => state.entity.cities.find((city) => city.id === cityId));
    const [stepsCompleted, setStepsCompleted] = useState<boolean[]>([true, false, true, true, true]);
    const [modelDataLoading, setModelDataLoading] = useState<boolean>(false);
    const {userAo, userCity, entityType} = useSelector((state: RootState) => state.entity);
    const [alternativeFieldsCompleted, setAlternativeFieldsCompleted] = useState<boolean>(false);
    const [updateLoading, setUpdateLoading] = useState<boolean>(false);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
    const {getAllCities} = useFetchAllEntities();
    const fetchCity = useFetchCity();

    const updateCity = () => {
        if(entityType === "ao") {
            getAllCities.mutate({ao_id: userAo?.id as string});
        }
        else {
            fetchCity.mutate({city_id: userCity?.id as string});
        }
    }

    const [alternativeFiles, setAlternativeFiles] = useState<UploadFilesTableData[]>([
        {
            type: "3dmodel",
            file: null,
            file_name: "",
            created_at: "",
            accept_types: [".3dm", ".glb", ".gltf", ".obj"]
        }
    ]);

    const [alternativeData, setAlternativeData] = useState<CreateAlternativeRequest>();

    const convertAlternativeDataToCreateAlternativeRequest = async (alternativeData: AlternativeData) => {
        const { files, id, created_by, created_at, residential_area, commercial_area, public_building_area, industrial_area, cultural_facilities_area, 
            transport_facilities_area, other_areas, sqm_facades_no_shade, facades_area, planned_additional_production, ...rest } = alternativeData;
        
        const baseData = { 
            ...rest,
            residential_area_planned: alternativeData.residential_area.graph_data["סיום פרויקט"] as number,
            residental_unit_area_planned: alternativeData.residental_unit_area_planned as number,
            commercial_area_planned: alternativeData.commercial_area.graph_data["סיום פרויקט"] as number,
            public_building_area_planned: alternativeData.public_building_area.graph_data["סיום פרויקט"] as number,
            industrial_area_planned: alternativeData.industrial_area.graph_data["סיום פרויקט"] as number,
            facades_area_north: facades_area?.north ?? 0,
            facades_area_south: facades_area?.south ?? 0,
            facades_area_east: facades_area?.east ?? 0,
            facades_area_west: facades_area?.west ?? 0,
            facades_area_total: facades_area?.total ?? 0,
            future_residential_parking: alternativeData.parking?.future_residential_parking,
            future_commercial_parking: alternativeData.parking?.future_commercial_parking,
            future_industrial_parking: alternativeData.parking?.future_industrial_parking,
            future_public_parking: alternativeData.parking?.future_public_parking,
            
        };

        // Only add planned_additional_production if it has a value
        if (alternativeData.planned_additional_production) {
            return {
                ...baseData,
                planned_additional_production: alternativeData.planned_additional_production
            };
        }

        return baseData;
    }

    const convertCreateDataToUpdateData = (createData: CreateAlternativeRequest) => {
        const { files, ...rest } = createData;
        return { 
            ...rest,
            alternative_id: alternativeId as string,
            files: files
        };
    }

    const getAlternative = useGetAlternative({
        async onSuccess(data) {
            const convertedData = await convertAlternativeDataToCreateAlternativeRequest(data);
            setAlternativeData(convertedData as CreateAlternativeRequest);

            // Update alternativeFiles with existing files
            if (data.files) {
                setAlternativeFiles(prev => prev.map(fileData => {
                    const file = data.files[fileData.type]
                    if (file) {
                        return {
                            ...fileData,
                            file_name: file.file_name,  // Extract filename from URL
                            created_at: file.created_at || currentDate(),
                            file: file.url
                        };
                    }
                    return fileData;
                }));

            }
        },
        onError(error) {
            console.error("Error in get alternative");
            dispatch(
                setToast({
                  toastTitle: error.response.data.message,
                  toastMessage: "",
                  toastType: "error",
                })
              );
            navigate("/404");
        }
    })

    useEffect(() => {
        getAlternative.mutate({alternative_id: alternativeId as string, project_id: projectId as string, city_id: cityId as string, ao_id: userAo?.id as string});
    }, [alternativeId]);
    
    const updateAlternative = useUpdateAlternative({
        onSuccess(data) {
            setUpdateLoading(false);
            dispatch(
                setToast({
                  toastTitle: data.message,
                  toastMessage: "",
                  toastType: "success",
                })
              );
            updateCity();
            navigate(`/${entityName}/project/${cityId}/${projectId}`);
        },
        onError(error) {
            setUpdateLoading(false);
            dispatch(
                setToast({
                  toastTitle: error.response.data.message,
                  toastMessage: "",
                  toastType: "error",
                })
            );
        },
    })

    const deleteAlternative = useDeleteAlternative({
        onSuccess(data) {
            setDeleteLoading(false);
            dispatch(
                setToast({
                  toastTitle: data.message,
                  toastMessage: "",
                  toastType: "success",
                })
            );
            navigate(`/${entityName}/project/${cityId}/${projectId}`);
        },
        onError(error) {
            setDeleteLoading(false);
            dispatch(
                setToast({
                  toastTitle: error.response.data.message,
                  toastMessage: "",
                  toastType: "error",
                })
            );
        }
    })

    useEffect(() => {
        if (!city && !userCity) {

            navigate("/404");
        }
    }, [city, userCity]);

    const extractModelData = useExtractModelData({
        onSuccess(data) {
            setAlternativeData({
                ...alternativeData,
                roofs_area: data.total_roof_area,
                facades_area_north: data.total_facades.north,
                facades_area_south: data.total_facades.south,
                facades_area_east: data.total_facades.east,
                facades_area_west: data.total_facades.west,
                facades_area_total: data.total_facades.total
            } as CreateAlternativeRequest);
            setModelDataLoading(false);
        },
        onError(error) {
            console.error("Error in extract Model Data");
            setModelDataLoading(false);
        }
    });

    const handleUpdateAlternativeData = (data: CreateAlternativeRequest) => {
        const newData = {
            ...data, 
            facades_area_total: (data.facades_area_north || 0) + 
                              (data.facades_area_south || 0) + 
                              (data.facades_area_east || 0) + 
                              (data.facades_area_west || 0)
        };
        setAlternativeData(newData);
    }

    const handleUploadFile = (type: FilesTypes, file: File, project: boolean) => {
        setModelDataLoading(true);
        const newAlternativeFiles = [...alternativeFiles];
        const index = newAlternativeFiles.findIndex(file => file.type === type);
        newAlternativeFiles[index] = {type, file, file_name: file.name, created_at: currentDate(), accept_types: alternativeFiles[index].accept_types};
        setAlternativeFiles(newAlternativeFiles);
         if (type === "3dmodel") {
                extractModelData.mutate({files: {"3dmodel": file}, ...getEntityIdPair(entityType, userCity, userAo)});
            }
    }

    const handleUpdateAlternative = () => {
        setUpdateLoading(true);
        const filesDict = alternativeFiles
            .filter(file => file.file && typeof(file.file) === "object")
            .reduce((acc, file) => ({
                ...acc,
                [file.type]: file.file
            }), {} as Record<FilesTypes, File>);
        let createData = convertCreateDataToUpdateData(alternativeData as CreateAlternativeRequest);   
        if (filesDict) {
            createData.files = filesDict;
        }
        // remove calculate fields
        const {
            consumption_per_sqm,
            energy_production,
            charging_stations_forecast,
            energy_resilience,
            electrical_infrastructure,
            parking,
            ...filteredData
        } = createData as any; 

        
        updateAlternative.mutate(filteredData);
    }

    const handleDeleteAlternative = () => {
        setDeleteLoading(true);
        deleteAlternative.mutate({alternative_id: alternativeId as string, project_id: projectId as string, city_id: cityId as string, ...getEntityIdPair(entityType, userCity, userAo)});

    }

    return (
        <>  
            <div className={styles.container}>
                <div className={styles.headerContainer}>
                    <h1 className={styles.title}>{t('updateAlternative')}</h1>
                    <div className={styles.backButtonContainer}>
                        <Button icon="left-side" text={t('delete')} id="delete-btn" onClick={handleDeleteAlternative} iconComponent={DeleteIcon} color="red" ready={true} className={styles.deleteBtn} loading={deleteLoading} disabled={updateLoading || deleteLoading}/>
                        <BackButton/>
                    </div>
                </div>
                <div className={styles.formContainer}>
                    {alternativeData ? (
                        <>
                            <CreateAlternative 
                                disabled={modelDataLoading}
                                onChange={handleUpdateAlternativeData} 
                                data={alternativeData} 
                                setStepsCompleted={setStepsCompleted} 
                                alternativeFiles={alternativeFiles} 
                                onUploadFile={(type, file) => handleUploadFile(type, file, false)} 
                                fieldsCompleted={alternativeFieldsCompleted} 
                                setFieldsCompleted={setAlternativeFieldsCompleted}
                            />
                            <div className={styles.stepsContainerOnePage}>
                                <div className={styles.stepsSubContainer}>
                                    <Button 
                                        icon="no" 
                                        text={t('updateButton')} 
                                        onClick={handleUpdateAlternative} 
                                        id="continue-btn" 
                                        className={styles.continueBtn} 
                                        disabled={!stepsCompleted[1] || updateLoading || deleteLoading || modelDataLoading} 
                                        loading={updateLoading}
                                    />
                                </div>
                            </div>
                        </>
                    ) : <LoadingPageAnimation/>}
                </div>
            </div>
        </>
    )
}